import { centsToDollarsFormatted } from "@/lib/currencyHelpers";
import Divider from "@/components/Divider";
import SectionHeading from "@/components/SectionHeading";
import { Event } from "../../../types";
import {
  buildPaymentSummaryLineItems,
  getPaymentSummaryTotals,
  normalizeEventFeePaymentCollection
} from "@/pages/events/eventFeeUtils";

interface PaymentSummaryProps {
  event: Event;
}

function PaymentSummaryContent({ event, eventFees }: { event: Event; eventFees: NonNullable<Event["eventFees"]> }) {
  const eventFeeItems = eventFees.flatMap((eventFee) => {
    if (eventFee.productVariant) {
      const amountCents = parseInt(eventFee.productVariant.priceCents ?? "0", 10);

      if (amountCents <= 0) {
        return [];
      }

      return [
        {
          name: eventFee.productVariant.name,
          amountCents,
          paymentCollection: normalizeEventFeePaymentCollection(eventFee.paymentCollection)
        }
      ];
    }

    if (!eventFee.displayName || !eventFee.amountCents) {
      return [];
    }

    return [
      {
        name: eventFee.displayName,
        amountCents: eventFee.amountCents,
        paymentCollection: "external_system" as const
      }
    ];
  });

  const fallbackPatItems = [];

  if (eventFeeItems.length === 0 && event.patEventAttributes?.registrationFeeCents) {
    fallbackPatItems.push({
      name: "Registration Fee",
      amountCents: event.patEventAttributes.registrationFeeCents,
      paymentCollection: "registration" as const
    });
  }

  if (eventFeeItems.length === 0 && event.patEventAttributes?.courseFeeCents) {
    fallbackPatItems.push({
      name: "Course Fee",
      amountCents: event.patEventAttributes.courseFeeCents,
      paymentCollection: normalizeEventFeePaymentCollection(event.patEventAttributes.collectionType)
    });
  }

  const lineItems = buildPaymentSummaryLineItems(eventFeeItems.length > 0 ? eventFeeItems : fallbackPatItems);

  // If no fees are available, don't render the payment summary
  if (lineItems.length === 0) {
    return null;
  }

  const { dueNowTotal, dueOnSiteTotal, externalSystemTotal, totalFees } = getPaymentSummaryTotals(lineItems);
  const showBreakdown = dueOnSiteTotal > 0 || externalSystemTotal > 0;

  return (
    <div>
      <SectionHeading heading="Payment Summary" body="Pricing information as seen by registrants during registration" />
      <div className="bg-sys-brand-surface-container-low rounded-lg">
        {lineItems.map((item, index: number) => (
          <div key={index} className="flex justify-between items-center text-body-normal py-2">
            <div className="flex flex-col">
              <span>{item.name}</span>
              {item.subText && (
                <span className="text-body-medium text-sys-brand-on-surface-variant">{item.subText}</span>
              )}
            </div>
            <span>{centsToDollarsFormatted(item.price)}</span>
          </div>
        ))}
        {lineItems.length > 0 && (
          <div className="py-3">
            <Divider />
          </div>
        )}
        <div className="flex justify-between text-title-medium-bold py-2">
          <span>Total Fees</span>
          <span>{centsToDollarsFormatted(totalFees)}</span>
        </div>
        {showBreakdown && (
          <>
            {dueOnSiteTotal > 0 && (
              <div className="flex justify-between text-body-normal py-2">
                <span>Due Onsite</span>
                <span>{centsToDollarsFormatted(dueOnSiteTotal)}</span>
              </div>
            )}
            <div className="flex justify-between text-body-normal py-2">
              <span>Due Now</span>
              <span>{centsToDollarsFormatted(dueNowTotal)}</span>
            </div>
            {externalSystemTotal > 0 && (
              <div className="flex justify-between text-body-normal py-2">
                <span>External System</span>
                <span>{centsToDollarsFormatted(externalSystemTotal)}</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentSummary({ event }: PaymentSummaryProps) {
  return <PaymentSummaryContent event={event} eventFees={event.eventFees ?? []} />;
}
